import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { SpecialUserPermissions } from "@app/enum/SpecialUserPermissions";
import { IPlot } from "@app/interfaces/IPlot";
import { FoundationSensorDataService } from "@app/main/modules/foundation/_services/sensor-data.service";
import { forkJoin, Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { AccountFeatureService } from "../accountFeature.service";
import { ConstantsService } from "../constantsService";
import { DateService, DateUnit } from "../date.service";
import { GoogleMarkerService } from "../googleMarker.service";
import { ReasonCodeService } from "../reasonCode.service";
import { TranslationService } from "../translation.service";
import { UsersService } from "../users.service";

@Injectable({
    providedIn: 'root'
})
export class PlotService {
    constructor(private readonly http: HttpClient,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly constantsService: ConstantsService,
        private readonly dateService: DateService,
        private readonly googleMarkerService: GoogleMarkerService,
        private readonly reasonCodeService: ReasonCodeService,
        private readonly sensorDataService: FoundationSensorDataService,
        private readonly translationService: TranslationService,
        private readonly usersService: UsersService) { }


    getLatestPlots(): Observable<IPlot[]> {
        return this.http.get<IPlot[]>('/api/vehicles/plots/latest');
    }

    getPlotsForDate(vehicleId: number, date: Date): Observable<IPlot[]> {
        const startDate = this.dateService.getStartOfDay(date);
        const endDate = this.dateService.getEndOfDay(date);

        return this.getPlotsForDateRange(vehicleId, startDate, endDate);
    }

    getPlotsForDateRange(vehicleId: number, start: moment.Moment | Date, end: moment.Moment | Date): Observable<IPlot[]> {
        if (!this.userCanAccessPlots()) {
            return of([]);
        }

        return this.http.get<IPlot[]>(`/api/vehicles/${vehicleId}/plots?plotOption=Basic&start=${start.toISOString()}&end=${end.toISOString()}`).pipe(map(plots => {
            const preparedPlots = this.preparePlotsForMap(plots);
            return preparedPlots;
        }));
    }

    getPlotsWithSensorData(vehicleId: number, start: Date, end: Date): Observable<IPlot[]> {
        return forkJoin({
            plots: this.getPlotsForDateRange(vehicleId, start, end),
            sensorData: this.sensorDataService.getSensorDataForVehicle(vehicleId, start, end)
        }).pipe(map(data => {
            const sensorDataMap = new Map(data.sensorData.map(data => [data.PlotId, data]));
            data.plots.forEach(plot => {
                plot.SensorData = sensorDataMap.get(plot.Id)
            });

            return data.plots;
        }))

    }

    getPlotNearestToTime(vehicleId: number, start: Date): Observable<IPlot> {

        const startTime = new Date(start);
        const endTime = this.dateService.addOrSubtractTime(startTime, 1, DateUnit.Hour);


        return this.getPlotsForDateRange(vehicleId, startTime, endTime).pipe(
            map(plots => {
                return plots[0];
            })
        );
    }

    convertDirectionToWords(degrees: number): string {
        const directionArray: string[] = [
            this.translationService.t('Common.North'),
            this.translationService.t('Common.NorthEast'),
            this.translationService.t('Common.East'),
            this.translationService.t('Common.SouthEast'),
            this.translationService.t('Common.South'),
            this.translationService.t('Common.SouthWest'),
            this.translationService.t('Common.West'),
            this.translationService.t('Common.NorthWest')
        ]

        const maxDirectionArrows = 8;
        const simpleNumber = (degrees / (360 / maxDirectionArrows));
        const val = Math.floor(simpleNumber + 0.5);

        const index = val % maxDirectionArrows;
        const arrayIndex = parseInt(index.toString(), 0);
        const compassDirection = directionArray[arrayIndex];

        return compassDirection;
    }

    private userCanAccessPlots(): boolean {
        return this.usersService.isCurrentUserOnFoundationPlatform() || this.accountFeatureService.doesCurrentUserHaveSpecialPermission(SpecialUserPermissions.ShowHistoricalPlots);
    }

    private preparePlotsForMap(plots: IPlot[], dontCreateOptions = false): IPlot[] {

        if (!plots) {
            return [];
        }

        for (let i = 0; i < plots.length; i++) {
            const plot = plots[i];
            plot.ReasonText = this.reasonCodeService.getReasonCodeLabel(plot.Reason);
            this.preparePlotForMap(plot);
        }

        // Plots are out of order, reverse for highcharts and display in correct (ascending time) order
        plots.sort((a, b) => { return new Date(a.Utc).getTime() - new Date(b.Utc).getTime(); });

        return plots;
    }

    private preparePlotForMap(plot: IPlot) {
        if (!plot) {
            return;
        }

        plot.markerModel = {
            id: plot.Id,
            options: this.googleMarkerService.createEventMarkerOptions({ label: "", position: { lat: plot.Latitude, lng: plot.Longitude } })
        }

        if (this.constantsService.getIsDemoSite() && plot.LocationInformation?.GeofenceIds?.length > 0) {
            plot.LocationInformation.LocationDescription = "Demo Geofence";
        }

        // This data is useful for side navigation  display
        if (plot.Utc) {
            // Small optimisation because we know plot Utcs come down in ISO format i.e. "2017-09-04T09:30:09Z"
            plot.FormattedDate = this.dateService.formatDate(plot.Utc);
        }
        else {
            console.warn(`Plot for ${plot.VehicleId} has no Utc '${plot.Utc}'`);
        }
    }
}
